<template>
  <div :class="{ 'audio-player': isAudioFile }" class="education-video-player">
    <div :class="{ 'audio-container': isAudioFile }" class="video-container">
      <!-- Loading indicator -->
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-spinner">
          <div class="spinner"></div>
          <p>{{ isAudioFile ? 'Ses dosyası yükleniyor...' : 'Video yükleniyor...' }}</p>
        </div>
      </div>

      <!-- Audio File Visual Design -->
      <div v-if="isAudioFile" class="audio-visual-container">
        <div class="audio-artwork">
          <div class="audio-icon-wrapper">
            <i class="icon-feather-volume-2 audio-main-icon"></i>
          </div>
          <div class="audio-info">
            <h3 class="audio-title">{{ selected_content.name }}</h3>
            <p class="audio-type">Ses Dosyası</p>
          </div>
        </div>
        <div class="audio-visualizer">
          <div class="audio-bars">
            <div v-for="i in 20" :key="i" :style="{ animationDelay: (i * 0.1) + 's' }" class="bar"></div>
          </div>
        </div>
      </div>

      <!-- Time display -->
      <div :class="{ 'audio-time-display': isAudioFile }" class="time-display">
        <div class="current_time">{{ formatTime(currentVideoTime) }}</div>
        <div class="total_time">{{ formatTime(totalDuration) }}</div>
      </div>

      <!-- YouTube embed -->
      <div v-if="isYouTube" class="youtube-embed-container">
        <iframe
            :src="youtubeEmbedUrl"
            :title="selected_content.name"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
        ></iframe>
      </div>

      <!-- Video/Audio element -->
      <video
          v-else
          id="education-video-player"
          :class="{ 'audio-player-element': isAudioFile }"
          :height="isAudioFile ? '400' : '400'"
          class="video-js vjs-default-skin vjs-big-play-centered"
          controls
          data-setup="{}"
          playsinline
          preload="metadata"
          width="100%"
      >
        <p class="vjs-no-js">
          {{ isAudioFile ? 'Ses dosyası' : 'Video' }} oynatmak için JavaScript etkin olmalıdır.
          <a :href="videoUrl" target="_blank">
            {{ isAudioFile ? 'Ses dosyasını' : 'Videoyu' }} doğrudan {{ isAudioFile ? 'dinle' : 'izle' }}
          </a>
        </p>
      </video>
    </div>
  </div>
</template>

<script>
import videojs from 'video.js';
import 'video.js/dist/video-js.min.css';
import 'videojs-contrib-hls';
import { GET_ITEM_DETAIL_BY_ID, UPDATE_ITEM_BY_ID } from "@/core/services/store/REST.module";
import {
  getVideoType,
  formatTime,
  adjustVideoSize,
  setupPageVisibility,
  cleanupPageVisibility,
  isYouTubeUrl,
  toYouTubeEmbedUrl
} from "@/utils/videojsUtils";

export default {
  name: "education-video-player",
  props: {
    selected_content: {
      type: Object,
      required: true
    },
    trainingId: {
      type: [String, Number],
      required: true
    },
    isDemo: {
      type: Boolean,
      default: false
    },
    pauseOnBackground: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      player: null,
      isLoading: false,
      updateContentData: {},
      currentDuration: 0,
      currentVideoTime: 0,
      totalDuration: 0,
      maxWatchedTime: 0,
    };
  },
  computed: {
    isAudioFile() {
      return this.selected_content?.training_file_type_id === 6 ||
          this.selected_content?.file_category?.key_name === 'documents.audio_document';
    },
    videoUrl() {
      // Önce file.file_link'i kontrol et (tam URL içerir)
      if (this.selected_content?.file?.file_link) {
        return this.selected_content.file.file_link;
      }

      // training_content varsa URL'i oluştur
      if (this.selected_content?.training_content) {
        return this.getFullUrl(this.selected_content.training_content);
      }

      return '';
    },
    isYouTube() {
      return isYouTubeUrl(this.videoUrl);
    },
    youtubeEmbedUrl() {
      return toYouTubeEmbedUrl(this.videoUrl);
    },

  },
  mounted() {
    if (this.isYouTube) return;

    this.$nextTick(() => {
      this.initVideoPlayer();

      if (this.pauseOnBackground) {
        setupPageVisibility(this.player, this);
      }
    });
  },
  beforeDestroy() {
    if (this.player) {
      this.player.dispose();
    }

    if (this.pauseOnBackground) {
      cleanupPageVisibility(this);
    }
  },
  methods: {
    formatTime,

    initVideoPlayer() {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      this.player = videojs('education-video-player', {
        controls: true,
        fluid: true,
        preload: 'metadata',
        playsinline: true,
        aspectRatio: '16:9',
        language: 'tr',
        controlBar: {
          pictureInPictureToggle: false,
          playbackRateMenuButton: true,
          fullscreenToggle: !isIOS,
        },
        playbackRates: [0.5, 1, 1.25, 1.5, 2],
      }, () => {
        this.setupVideoSource();
        this.setupVideoEvents();
      });
    },

    setupVideoSource() {
      if (!this.videoUrl) {
        console.warn('Video kaynağı bulunamadı');
        return;
      }

      this.player.src({
        src: this.videoUrl,
        type: getVideoType(this.videoUrl)
      });

      this.player.load();
    },

    setupVideoEvents() {
      this.player.on('loadstart', () => {
        this.isLoading = true;
        if (this.player && this.player.el()) {
          this.player.el().classList.add('vjs-loading');
        }
      });

      this.player.on('loadedmetadata', () => {
        this.totalDuration = this.player.duration();
        adjustVideoSize(this.player, this.$el, 'education-video');

        // Demo değilse kullanıcının kaldığı yeri al
        if (!this.isDemo) {
          this.getVideoWhereIStay(this.selected_content.id);
        } else {
          this.isLoading = false;
          this.removeBigPlayButton();
        }
      });

      this.player.on('loadeddata', () => {
        if (this.currentDuration > 0) {
          this.setVideoPosition(this.currentDuration);
        }
      });

      this.player.on('error', () => {
        this.isLoading = false;
        this.handleVideoError();
      });

      this.player.on('pause', () => {
        if (!this.isDemo) {
          this.updateVideoWhereStay();
        }
      });

      this.player.on('ended', () => {
        setTimeout(() => {
          this.$emit('video-ended', this.selected_content);
        }, 500);
      });

      this.player.on('timeupdate', () => {
        this.currentVideoTime = this.player.currentTime();
        if (this.currentVideoTime > this.maxWatchedTime) {
          this.maxWatchedTime = this.currentVideoTime;
        }
      });
    },

    handleVideoError() {
      this.isLoading = false;
      alert("Video oynatılamadı. Lütfen bağlantınızı kontrol edip tekrar deneyin.");
    },

    updateVideoWhereStay() {
      if (!this.player || !this.selected_content) return false;

      const currentTime = this.player.currentTime();
      const duration = this.player.duration();

      if (!duration || isNaN(duration)) return false;

      const maxTime = Math.max(this.maxWatchedTime, currentTime);
      const completionPercentage = Math.round((maxTime * 100) / duration);

      this.updateContentData = {
        id: this.selected_content.id,
        completion_percentage: completionPercentage,
        duration: maxTime,
      };

      this.$store.dispatch(UPDATE_ITEM_BY_ID, {
        url: `api/training/update-content/${this.trainingId}/${this.selected_content.id}`,
        contents: this.updateContentData,
        noLoading: true,
      }).then(() => {
        this.$emit('video-progress-updated', this.updateContentData);
      }).catch((error) => {
        console.error('Error updating video position:', error);
      });
    },

    getVideoWhereIStay(content_id) {
      let url = `api/training/${this.trainingId}/training-content/${content_id}/stay`;

      this.$store.dispatch(GET_ITEM_DETAIL_BY_ID, {
        url: url,
        acceptPromise: false,
      }).then((result) => {
        if (result.status) {
          this.currentDuration = result.data || 0;
          this.setVideoPosition(this.currentDuration);
        } else {
          this.isLoading = false;
          this.removeBigPlayButton();
        }
      }).catch((error) => {
        console.error('Video pozisyonu alınamadı:', error);
        this.isLoading = false;
        this.removeBigPlayButton();
      });
    },

    setVideoPosition(position) {
      if (!this.player || !this.player.duration()) {
        setTimeout(() => {
          this.setVideoPosition(position);
        }, 500);
        return;
      }

      const duration = this.player.duration();

      if (position >= duration) {
        this.player.currentTime(0);
      } else {
        this.player.currentTime(position);
      }

      setTimeout(() => {
        this.isLoading = false;
        this.removeBigPlayButton();
      }, 600);
    },

    removeBigPlayButton() {
      if (this.player && this.player.el()) {
        this.player.el().classList.remove('vjs-loading');
      }
    },

    // URL'i tam URL'e çevirir
    getFullUrl(url) {
      if (!url) return '';

      // Zaten tam URL ise direkt döndür
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
      }

      // Göreli yol ise base URL ekle
      const baseStorageUrl = process.env.VUE_APP_BACKEND_APP_URL || 'https://api.zorunluegitimler.com';
      return `${baseStorageUrl}/storage/${url}`;
    },

    // Modal kapandığında çağrılır - video pozisyonunu günceller - ref ile tetikleniyor, silinmesin
    onModalClose() {
      if (!this.isDemo) {
        this.updateVideoWhereStay();
      }
    },

  }
}
</script>

<style scoped src="./education-video-player.css"/>
